/**
 * PillTabs Component
 *
 * Reusable horizontal scrollable pill-style tabs for filtering
 * with count badges and active state styling
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PillTabsProps } from './PillTabs.types';
import styles from './PillTabs.module.css';

export const PillTabs = memo(function PillTabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  variant = 'default',
  flush = false,
}: PillTabsProps<T>) {
  const { t } = useTranslation();

  const variantClass =
    variant === 'compact'
      ? styles.compact
      : variant === 'inline'
        ? styles.inline
        : '';

  return (
    <div
      className={`${styles.tabsSection} ${variantClass} ${flush ? styles.flush : ''}`}
    >
      <div className={styles.tabsList}>
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={`${styles.tab} ${activeTab === tab.value ? styles.active : ''}`}
            onClick={() => onTabChange(tab.value)}
            aria-pressed={activeTab === tab.value}
          >
            <span>{t(tab.labelKey)}</span>
            {tab.count !== undefined && (
              <span className={styles.tabBadge}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}) as <T extends string>(props: PillTabsProps<T>) => React.ReactElement;
